import {
  useLayoutEffect,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { ProgressBar } from "devextreme-react/progress-bar";
import DataGrid, {
  Column,
  Export,
  HeaderFilter,
  Pager,
  Paging,
  SearchPanel,
} from "devextreme-react/data-grid";
import { FileType } from "../../../helper/fixcode";
import { useNavigate } from "react-router-dom";
import "./../../../App.css";
import { useSelector } from "react-redux";
import moment from "moment";
import {
  showErrorMessage,
  showResponseMessage,
  showWarningMessage,
} from "../../../helper/message";
import { Loader } from "../../Shared/PageLoader";
import { uploadFileService } from "../../../apis/apiServices/UploadFile";
import * as XLSX from "xlsx";

export default function UploadSubCategoryComponent(props) {
  const user = useSelector((state) => state.user.data);
  const navigate = useNavigate();
  const [state, setState] = useState({
    isCompleteLoading: false,
    isLoading: false,
    loadingMessage: "",
    subCategoryBulkDataSourceLst: [],
    importSubCategoryDataLst: [],
    importSubCategoryCount: 0,
    saveImportedSubCategoryCount: 0,
    processSubCategoryCount: 0,
  });
  const refDataGrid = useRef(null);
  const refIsImporting = useRef(false);
  const refIsProcessing = useRef(false);

  useLayoutEffect(() => {
    document.querySelector("#pageTitle").innerHTML = `Upload Sub Category`;
  }, []);

  let isUseEffectRendered = false;
  useEffect(() => {
    const controller = new AbortController();
    try {
      if (isUseEffectRendered) {
        return;
      }
      fetchApiOnPageLoad();
      return () => {
        controller.abort();
        isUseEffectRendered = true;
      };
    } catch (error) {
      console.log(error);
    }
  }, []);

  const fetchApiOnPageLoad = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isCompleteLoading: true }));
      await getSubCategoryBulkLst();
      setState((prev) => ({ ...prev, isCompleteLoading: false }));
    } catch (error) {
      console.log(error);
      setState((prevState) => ({
        ...prevState,
        isCompleteLoading: false,
      }));
    }
  }, []);

  const getSubCategoryBulkLst = async () => {
    try {
      const response = await uploadFileService.getSubCategoryBulkDataList(
        Number(FileType.SubCategory)
      );
      setState((prev) => ({
        ...prev,
        subCategoryBulkDataSourceLst: response?.data ?? [],
      }));
    } catch (error) {
      console.log(error);
    }
  };

  const handleOnBrowseFile = async (e) => {
    try {
      setState((prev) => ({ ...prev, isLoading: true }));
      await uploadFileService.truncateSubCategoryBulkData();
      const file = e?.target?.files[0];
      const bufferArray = await file.arrayBuffer();
      const workbook = XLSX.read(bufferArray, { type: "buffer" });
      if (workbook.SheetNames.length > 1) {
        await showErrorMessage(
          "The document contains multiple worksheets. Only one worksheet is allowed in the workbook."
        );
        setState((prev) => ({ ...prev, isLoading: false }));
        document.getElementById("browsefile").value = null;
        return;
      }
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: null });
      if (!jsonData || jsonData.length === 0) {
        await showWarningMessage(
          "The uploaded Excel file is empty. Please fill at least one row and try again."
        );
        setState((prev) => ({ ...prev, isLoading: false }));
        document.getElementById("browsefile").value = null;
        return;
      }

      //Column Validation
      //#region
      const requiredColumns = ["Name", "Code", "Category Name"];
      let rowErrors = {};
      jsonData.forEach((row, index) => {
        const missingCols = [];
        requiredColumns.forEach((col) => {
          const value = row[col];
          if (
            value === null ||
            value === undefined ||
            value.toString().trim() === ""
          ) {
            missingCols.push(col);
          }
        });

        if (missingCols.length > 0) {
          rowErrors[index + 2] = missingCols;
        }
      });
      if (Object.keys(rowErrors).length > 0) {
        const errorLines = Object.entries(rowErrors).map(
          ([row, cols]) => `Row ${row} → Missing column(s): ${cols.join(", ")}`
        );
        await showErrorMessage(
          `Please fill the following column(s):\n\n• ${errorLines.join("\n• ")}`
        );
        setState((prev) => ({ ...prev, isLoading: false }));
        document.getElementById("browsefile").value = null;
        return;
      }
      //#endregion

      const cols = ["Name", "Code", "Category Name"];
      var colKeys = jsonData.length > 0 ? Object.keys(jsonData[0]) : [];
      const normalize = (str) =>
        str?.toString().trim().toLowerCase().replace(/\s+/g, " ");
      const normalizedExpected = cols.map(normalize);
      const normalizedActual = colKeys.map(normalize);
      const missingColumns = cols.filter(
        (col) => !normalizedActual.includes(normalize(col))
      );
      const extraColumns = colKeys.filter(
        (col) => !normalizedExpected.includes(normalize(col))
      );
      //let isValidFile = cols.every((col) => colKeys.includes(col));
      const isValidFile =
        missingColumns.length === 0 && extraColumns.length === 0;
      if (isValidFile) {
        let rowData = null;
        rowData = [
          ...new Set(
            jsonData?.map((item) => ({
              SubCategoryName: item["Name"]?.toString() ?? "",
              SubCategoryCode: item["Code"]?.toString() ?? "",
              CategoryName: item["Category Name"]?.toString() ?? "",
            }))
          ),
        ];
        setState((prev) => ({
          ...prev,
          importSubCategoryDataLst: rowData,
          importSubCategoryCount: rowData?.length,
        }));
        await handleOnSaveImportedSubCategory(
          rowData?.length,
          rowData,
          file?.name
        );
        setState((prev) => ({ ...prev, isLoading: false }));
      } else {
        let errorMsg = "";
        if (missingColumns.length > 0) {
          errorMsg += `Missing column(s):\n• ${missingColumns.join(
            "\n• "
          )}\n\n`;
        }
        if (extraColumns.length > 0) {
          errorMsg += `Unexpected / misspelled column(s):\n• ${extraColumns.join(
            "\n• "
          )}`;
        }
        await showErrorMessage(errorMsg.trim());
        setState((prev) => ({ ...prev, isLoading: false }));
        document.getElementById("browsefile").value = null;
      }
    } catch (error) {
      console.log(error);
      setState((prev) => ({ ...prev, isLoading: false }));
      document.getElementById("browsefile").value = null;
    }
  };

  const handleOnSaveImportedSubCategory = async (count, rowData, fileName) => {
    try {
      if (count === 0 || refIsImporting?.current) {
        return;
      }
      refIsImporting.current = true;
      let subCategoryDataLst = [
        ...new Set(
          rowData?.map((item, index) => ({
            ...item,
            rowId: index + 1,
          }))
        ),
      ];
      let totalSycnced = 0;
      let batchSize = 10;
      let totalSubCategory = subCategoryDataLst?.length ?? 0;
      if (totalSubCategory > 100) {
        batchSize = Math.ceil((totalSubCategory / 100) * 2);
      }
      let isSuccess = false;
      let importFailedCount = 0;
      for (let index = 0; index < totalSubCategory; index = index + batchSize) {
        if (index > 0 && !refIsImporting?.current) {
          break;
        }
        let newSubCategoryDataLst = [
          ...new Set(subCategoryDataLst?.slice(index, index + batchSize)),
        ];
        if (newSubCategoryDataLst?.length === 0) {
          break;
        }
        try {
          let body = {
            userId: user?.userId,
            fileName: fileName,
            uploadSubCategoryBulkDataLst: newSubCategoryDataLst,
          };
          const response = await uploadFileService.uploadSubCategory(body);
          if (response?.isSuccess) {
            isSuccess = true;
          } else {
            importFailedCount += 1;
          }
        } catch (resError) {
          console.log(resError);
          importFailedCount += 1;
        }
        totalSycnced += newSubCategoryDataLst?.length ?? 0;
        setState((prev) => ({
          ...prev,
          saveImportedSubCategoryCount: totalSycnced,
        }));
        refDataGrid?.current?.instance?.repaint();
        refDataGrid?.current?.instance?.refresh();
        if (totalSycnced >= totalSubCategory) {
          break;
        }
      }
      refIsImporting.current = false;
      setState((prev) => ({
        ...prev,
        importSubCategoryDataLst: [],
        saveImportedSubCategoryCount: totalSycnced,
      }));
      if (totalSycnced > 0) {
        if (isSuccess) {
          let message = `Sub Category data(s) [ ${
            totalSycnced - importFailedCount
          } ] has been imported successfully.`;
          await showResponseMessage({
            isSuccess: true,
            message: message,
          });
          await getSubCategoryBulkLst();
        } else {
          await showErrorMessage(`Failed to import sub category data`);
        }
        if (importFailedCount > 0 && totalSubCategory > 0) {
          await showErrorMessage(`Some of sub category data failed to import`);
        }
      }
      document.getElementById("browsefile").value = null;
      handleOnResetImporting();
    } catch (error) {
      console.log(error);
      refIsImporting.current = false;
    }
  };

  const handleOnResetImporting = () => {
    try {
      setState((prev) => ({
        ...prev,
        importSubCategoryDataLst: [],
        importSubCategoryCount: 0,
        saveImportedSubCategoryCount: 0,
      }));
      refIsImporting.current = false;
    } catch (error) {
      console.log(error);
    }
  };

  const handleOnProcessSubCategory = async (e) => {
    e.preventDefault();
    try {
      let pendingProcessSubCategoryDetails =
        state.subCategoryBulkDataSourceLst.filter(
          (f) => !f.isProcessed && f.isValid
        );
      if (
        pendingProcessSubCategoryDetails?.length === 0 &&
        refIsProcessing?.current
      ) {
        return;
      }
      refIsProcessing.current = true;
      let pendingSubCategoryLst = [
        ...new Set(
          pendingProcessSubCategoryDetails?.map((item, index) => ({
            ...item,
            rowId: index + 1,
          }))
        ),
      ];
      let totalSycnced = 0;
      let batchSize = 10;
      let totalSubCategory = pendingSubCategoryLst?.length ?? 0;
      if (totalSubCategory > 100) {
        batchSize = Math.ceil((totalSubCategory / 100) * 2);
      }
      let isSuccess = false;
      let processFailedCount = 0;
      for (let index = 0; index < totalSubCategory; index = index + batchSize) {
        if (index > 0 && !refIsProcessing?.current) {
          break;
        }
        let subCategoryDetails = [
          ...new Set(pendingSubCategoryLst?.slice(index, index + batchSize)),
        ];
        if (subCategoryDetails?.length === 0) {
          break;
        }
        try {
          let body = {
            subCategoryBulkDataLst: subCategoryDetails,
            userId: user?.userId,
          };
          const response = await uploadFileService.processSubCategory(body);
          if (response.isSuccess) {
            isSuccess = true;
          } else {
            processFailedCount += 1;
          }
        } catch (resError) {
          console.log(resError);
          processFailedCount += 1;
        }
        totalSycnced += subCategoryDetails?.length ?? 0;
        setState((prev) => ({
          ...prev,
          processSubCategoryCount: totalSycnced,
          subCategoryBulkDataSourceLst: subCategoryDetails,
        }));
        refDataGrid?.current?.instance?.repaint();
        refDataGrid?.current?.instance?.refresh();
        if (totalSycnced >= totalSubCategory) {
          break;
        }
      }
      refIsProcessing.current = false;
      setState((prev) => ({
        ...prev,
        processSubCategoryCount: totalSycnced,
      }));
      if (totalSycnced > 0) {
        if (isSuccess) {
          await getSubCategoryBulkLst();
          await showResponseMessage({
            isSuccess: true,
            message: "Sub Category processed successfully!!",
          });
        } else if (processFailedCount > 0 && totalSubCategory > 0) {
          setState((prev) => ({
            ...prev,
            processSubCategoryCount: 0,
          }));
          await showErrorMessage("Some of sub category failed to process.");
          return;
        } else {
          setState((prev) => ({
            ...prev,
            processSubCategoryCount: 0,
          }));
          await showErrorMessage("Failed to process sub category.");
          return;
        }
      }
      await handleOnResetProcessing();
    } catch (error) {
      console.log(error);
      refIsProcessing.current = false;
    }
  };

  const handleOnResetProcessing = async () => {
    try {
      setState((prev) => ({
        ...prev,
        processSubCategoryCount: 0,
      }));
      refIsProcessing.current = false;
    } catch (error) {
      console.log(error);
    }
  };

  const importingSubCategoryProgressStatusFormat = useCallback(
    (value, ratio) => {
      try {
        let statusTitle = value * 100 === 100 ? "Completed" : "Syncing";
        return `${statusTitle} - ${
          (value * 100).toFixed(0) + "% (" + ratio + ")"
        }`;
      } catch (error) {
        console.log(error);
      }
    },
    [state.importSubCategoryCount]
  );

  const processingSubCategoryProgressStatusFormat = useCallback(
    (value, ratio) => {
      try {
        let statusTitle = value * 100 === 100 ? "Completed" : "Syncing";
        return `${statusTitle} - ${
          (value * 100).toFixed(0) + "% (" + ratio + ")"
        }`;
      } catch (error) {
        console.log(error);
      }
    },
    [state.processSubCategoryCount]
  );

  return (
    <>
      <div className="content">
        <div className="card" style={{ height: "70px" }}>
          <div className="card-body">
            <div className="row">
              <div
                className="col-12 col-lg-6"
                // className={`col-12 col-lg-${
                //   (state.subCategoryBulkDataSourceLst?.filter(
                //     (f) => f.isProcessed && f.isValid
                //   )?.length &&
                //     state.subCategoryBulkDataSourceLst?.length) ||
                //   state.subCategoryBulkDataSourceLst?.length === 0
                //     ? `6`
                //     : `6`
                // }`}
              >
                <div className="form-group mb-2">
                  <button
                    type="button"
                    title="Back"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => {
                      try {
                        navigate("/SubCategory");
                      } catch (error) {
                        console.log(error);
                      }
                    }}
                  >
                    <span title="Back ">
                      <i className="fas fa-arrow-left" />
                      &nbsp;&nbsp;Back
                    </span>
                  </button>
                  &nbsp;&nbsp;&nbsp;
                  <button
                    type="button"
                    title="Download Sample File"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={(e) => {
                      try {
                        e.preventDefault();
                        window.location.href =
                          "./assets/excel/SampleSubCategory.xlsx";
                      } catch (error) {
                        console.log(error);
                      }
                    }}
                  >
                    <span title="Download Sample File">
                      <i className="fas fa-file-download"></i>
                      &nbsp;&nbsp;Download Sample File
                    </span>
                  </button>
                  &nbsp;&nbsp;&nbsp;
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    title="Browse File"
                    onClick={() => {
                      document.getElementById("browsefile").click();
                    }}
                    disabled={state.isLoading}
                  >
                    <span title="Browse File">
                      <i
                        className={`fas fa-${
                          state.isLoading ? "spinner fa-spin" : "file-import"
                        }`}
                      />
                      &nbsp;&nbsp;Browse File
                    </span>
                  </button>
                  <input
                    id="browsefile"
                    type="file"
                    className="input"
                    onChange={async (e) => {
                      await handleOnBrowseFile(e);
                    }}
                    hidden={true}
                  />
                  {state.importSubCategoryCount === 0 &&
                    state.processSubCategoryCount === 0 && (
                      <>
                        &nbsp;&nbsp;&nbsp;
                        <button
                          type="button"
                          title="Process"
                          className="btn btn-outline-primary btn-sm"
                          onClick={async (e) => {
                            await handleOnProcessSubCategory(e);
                          }}
                          hidden={
                            (state.subCategoryBulkDataSourceLst?.filter(
                              (f) => f.isProcessed && f.isValid
                            )?.length &&
                              state.subCategoryBulkDataSourceLst?.length) ||
                            state.subCategoryBulkDataSourceLst?.length === 0
                          }
                          disabled={
                            state.processSubCategoryCount > 0 ||
                            refIsProcessing?.current
                          }
                        >
                          <i
                            className={`${
                              refIsProcessing?.current
                                ? "fas fa-spinner fa-spin"
                                : "fas fa-filter"
                            }`}
                          />
                          &nbsp;Process
                        </button>
                      </>
                    )}
                </div>
              </div>
              <div className="col-12 col-lg-4">
                {state.importSubCategoryCount > 0 && (
                  <>
                    <span style={{ fontSize: "Medium" }}>
                      {`Category Left : (${
                        state.importSubCategoryCount -
                        Number(state.saveImportedSubCategoryCount)
                      }/${state.importSubCategoryCount})`}
                    </span>
                    <ProgressBar
                      width="100%"
                      min={0}
                      max={state.importSubCategoryCount}
                      value={Number(state.saveImportedSubCategoryCount)}
                      statusFormat={importingSubCategoryProgressStatusFormat}
                    />
                  </>
                )}
                {state.processSubCategoryCount > 0 && (
                  <>
                    <span style={{ fontSize: "Medium" }}>
                      {`Processing Category: (${
                        state.subCategoryBulkDataSourceLst?.length -
                        Number(state.processSubCategoryCount)
                      }/${state.subCategoryBulkDataSourceLst?.length})`}
                    </span>
                    <ProgressBar
                      width="100%"
                      min={0}
                      max={state.subCategoryBulkDataSourceLst?.length}
                      value={Number(state.processSubCategoryCount)}
                      statusFormat={processingSubCategoryProgressStatusFormat}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <DataGrid
              ref={refDataGrid}
              keyExpr="id"
              dataSource={state.subCategoryBulkDataSourceLst}
              showColumnLines={false}
              showRowLines={true}
              showBorders={true}
              allowColumnResizing={false}
              allowColumnReordering={true}
              columnAutoWidth={true}
              onCellPrepared={(e) => {
                if (e.rowType === "data") {
                  if (e?.data?.isValid) {
                    e.cellElement.style.backgroundColor = "#f8f8f8ff";
                  } else {
                    e.cellElement.style.backgroundColor = "#fc6060ff";
                  }
                }
              }}
              onToolbarPreparing={(e) => {
                const items = [];
                items.push({
                  location: "after",
                  widget: "dxButton",
                  options: {
                    icon: "refresh",
                    text: "Refresh",
                    hint: "Refresh",
                    type: "default",
                    stylingMode: "outlined",
                    onClick: async () => {
                      getSubCategoryBulkLst();
                    },
                  },
                });
                e.toolbarOptions.items.unshift(...items);
              }}
            >
              <Column
                dataField="subCategoryName"
                caption="Name"
                allowSearch={true}
                allowFiltering={false}
                allowSorting={true}
              />
              <Column
                dataField="subCategoryCode"
                caption="Code"
                allowSearch={true}
                allowFiltering={false}
                allowSorting={true}
              />
              <Column
                dataField="categoryName"
                caption="Category"
                allowSearch={true}
                allowFiltering={false}
                allowSorting={true}
              />
              <Column
                dataField="createdDate"
                caption="Created On"
                allowSearch={true}
                allowFiltering={false}
                calculateCellValue={(data) => {
                  return `${data?.createdDate || ""} ${
                    data?.createdByUserName || ""
                  }`;
                }}
                cellRender={(data) => {
                  if (data?.data?.createdDate) {
                    return (
                      <>
                        <span>
                          {moment(data?.data?.createdDate).format(
                            "DD/MM/YYYY"
                          ) +
                            " " +
                            moment(data?.data?.createdDate).format("LT") ?? ""}
                        </span>
                        <br />
                        <div style={{ color: "#000000ff", fontSize: "11px" }}>
                          {data?.data?.createdByUserName ?? ""}
                        </div>
                      </>
                    );
                  } else {
                    return "";
                  }
                }}
              />
              <Column
                dataField="remark"
                caption="Remark"
                allowSearch={true}
                allowFiltering={false}
                allowSorting={true}
              />
              <HeaderFilter visible={false} />
              <SearchPanel
                visible={true}
                highlightCaseSensitive={true}
                width={240}
                placeholder="Search..."
              />
              <Paging defaultPageSize={10} />
              <Pager
                allowedPageSizes={[10, 15, 20, 50, 100]}
                showPageSizeSelector={true}
                showInfo={true}
                visible={true}
                showNavigationButtons={true}
              />
              <Export enabled={true} fileName="Sub Category Bulk Data" />
            </DataGrid>
          </div>
        </div>
      </div>
      {state.isCompleteLoading && (
        <Loader loadingMessage={state.loadingMessage} />
      )}
    </>
  );
}
